'use strict';

var version = "0.0.1";

/**
 * @function BufferTracking
 * @param    {Object} [options={}]
 *           An object of options left to the plugin author to define.
 *
 * Tracks when the video player is marked as buffering and waits until the player
 * has made some progress.
 *
 * Example Usage:
 * player.on('tracking:buffered', (e, data) => console.log(data))
 *
 * Data Attributes:
 * => currentTime:    current second of video playback
 * => readyState:     video#readyState value
 * => secondsToLoad:  Total amount of time in seconds buffering took
 * => bufferCount:    Total buffer events for this source
 */

var BufferTracking = function BufferTracking(config) {
  var player = this;
  var timer = null;
  var scrubbing = false;
  var bufferPosition = false;
  var bufferStart = false;
  var bufferEnd = false;
  var bufferCount = 0;
  var readyState = false;

  var reset = function reset() {
    if (timer) {
      clearTimeout(timer);
    }
    scrubbing = false;
    bufferPosition = false;
    bufferStart = false;
    bufferEnd = false;
    bufferCount = 0;
    readyState = false;
  };

  player.on('dispose', reset);
  player.on('loadstart', reset);
  player.on('ended', reset);
  player.on('pause', function () {
    if (player.scrubbing()) {
      scrubbing = true;
      bufferStart = false;
      timer = setTimeout(function () {
        scrubbing = false;
      }, 200);
    }
  });

  player.on('waiting', function () {
    if (bufferStart == false && scrubbing === false && player.currentTime() > 0) {
      bufferStart = new Date();
      bufferPosition = +player.currentTime().toFixed(0);
      readyState = +player.readyState();
    }
  });

  player.on('timeupdate', function () {
    var curTime = +player.currentTime().toFixed(0);

    if (bufferStart && curTime != bufferPosition) {
      bufferEnd = new Date();

      var secondsToLoad = (bufferEnd - bufferStart) / 1000;

      bufferStart = false;
      bufferPosition = false;
      bufferCount++;

      player.trigger('tracking:buffered', {
        currentTime: +curTime,
        readyState: +readyState,
        secondsToLoad: +secondsToLoad.toFixed(3),
        bufferCount: +bufferCount
      });
    }
  });
};

/**
 * Tracks when users pause the video.
 *
 * Example Usage:
 * player.on('tracking:pause', (e, data) => console.log(data))
 *
 * Data Attributes:
 * => pauseCount:       Total number of Pause events triggered
 *
 * @function PauseTracking
 * @param    {Object} [options={}]
 *           An object of config left to the plugin author to define.
 */

var PauseTracking = function PauseTracking(options) {
  var player = this;
  var pauseCount = 0;
  var timer = null;
  var locked = false;
  var reset = function reset(e) {
    if (timer) {
      clearTimeout(timer);
    }
    pauseCount = 0;
    locked = false;
  };

  player.on('dispose', reset);
  player.on('loadstart', reset);
  player.on('ended', reset);
  player.on('pause', function () {
    if (player.scrubbing() || locked) {
      return;
    }

    timer = setTimeout(function () {
      pauseCount++;
      player.trigger('tracking:pause', { pauseCount: pauseCount });
    }, 300);
  });
};

/**
 * Track Overall Percentile (1st, 2nd, 3rd, and 4th) of Completion
 * This event triggers each quarter of a video.
 *
 * Example Usage:
 * player.on('tracking:first-quarter', (e, data) => console.log(data))
 * player.on('tracking:second-quarter', (e, data) => console.log(data))
 * player.on('tracking:third-quarter', (e, data) => console.log(data))
 * player.on('tracking:fourth-quarter', (e, data) => console.log(data))
 *
 * Data Attributes:
 * => pauseCount:       Total number of Pause events triggered
 * => seekCount:        Total number of Seek events triggered
 * => currentTime:      Current second video is on
 * => duration:         Total duration of video
 *
 * @function PercentileTracking
 * @param    {Object} [config={}]
 *           An object of config left to the plugin author to define.
 */

var PercentileTracking = function PercentileTracking(config) {

    var player = this;
    var first = false;
    var second = false;
    var third = false;
    var duration = 0;
    var pauseCount = 0;
    var seekCount = 0;

    var reset = function reset(e) {
        first = false;
        second = false;
        third = false;
        duration = 0;
        pauseCount = 0;
        seekCount = 0;
    };

    var incPause = function incPause() {
        return pauseCount++;
    };
    var incSeek = function incSeek() {
        return seekCount++;
    };

    player.on('dispose', reset);
    player.on('loadstart', reset);
    player.on('tracking:pause', incPause);
    player.on('tracking:seek', incSeek);
    player.on('timeupdate', function () {
        var curTime = +player.currentTime().toFixed(0);
        var data = {
            seekCount: seekCount,
            pauseCount: pauseCount,
            currentTime: curTime,
            duration: duration
        };
        config["seekCount"] = data.seekCount;
        config["pauseCount"] = data.pauseCount;
        config["currentTime"] = data.currentTime;
        config["duration"] = data.duration;
        config["timecode"] = curTime;
        switch (curTime) {
            case first:
                first = false;
                player.trigger('tracking:first-quarter', config);
                break;
            case second:
                second = false;
                player.trigger('tracking:second-quarter', config);
                break;
            case third:
                third = false;
                player.trigger('tracking:third-quarter', config);
                break;
        }
    });
    player.on('ended', function () {
        var data = {
            seekCount: seekCount,
            pauseCount: pauseCount,
            currentTime: duration,
            duration: duration
        };
        config["seekCount"] = data.seekCount;
        config["pauseCount"] = data.pauseCount;
        config["currentTime"] = data.currentTime;
        config["duration"] = data.duration;
        config["x-event"] = "fourth-quarter";
        player.trigger('tracking:fourth-quarter', config);
    });

    player.on('durationchange', function () {
        duration = +player.duration().toFixed(0);
        var quarter = (duration / 4).toFixed(0);

        first = +quarter;
        second = +quarter * 2;
        third = +quarter * 3;
    });
};

/**
 * Track Overall Performance
 * This event triggers when the player has changed sources, has ended, or
 * has been destroyed.
 *
 * Example Usage:
 * player.on('tracking:performance', (e, data) => console.log(data))
 *
 * Data Attributes:
 * => pauseCount:       Total number of Pause events triggered
 * => seekCount:        Total number of Seek events triggered
 * => bufferCount:      Total number of Buffer events triggered
 * => totalDuration:    Total duration provided by the file
 * => watchedDuration:  Total number of seconds watched, this excluses seconds a user has seeked past.
 * => bufferDuration:   Total seconds that buffering has occured
 * => initialLoadTime:  Seconds it took for the initial frame to appear
 *
 * @function PerformanceTracking
 * @param    {Object} [config={}]
 *           An object of config left to the plugin author to define.
 */
var PerformanceTracking = function PerformanceTracking(config) {

  var player = this;
  var seekCount = 0;
  var pauseCount = 0;
  var bufferCount = 0;
  var totalDuration = 0;
  var watchedDuration = 0;
  var bufferDuration = 0;
  var initialLoadTime = 0;
  var timestamps = [];

  var reset = function reset() {
    seekCount = 0;
    pauseCount = 0;
    bufferCount = 0;
    totalDuration = 0;
    watchedDuration = 0;
    bufferDuration = 0;
    initialLoadTime = 0;
    timestamps = [];
  };

  var trigger = function trigger() {
    var data = {
      pauseCount: pauseCount,
      seekCount: seekCount,
      bufferCount: bufferCount,
      totalDuration: totalDuration,
      watchedDuration: watchedDuration,
      bufferDuration: bufferDuration,
      initialLoadTime: initialLoadTime
    };
    player.trigger('tracking:performance', data);
  };

  var triggerAndReset = function triggerAndReset() {
    trigger();
    reset();
  };

  player.on('loadstart', function () {
    if (totalDuration > 0) {
      trigger();
    }
    reset();
  });
  player.on('ended', triggerAndReset);
  player.on('dispose', triggerAndReset);
  player.on('timeupdate', function () {
    var curTime = +player.currentTime().toFixed(0);

    if (timestamps.indexOf(curTime) < 0) {
      timestamps.push(curTime);
    }
    watchedDuration = timestamps.length;
  });

  player.on('loadeddata', function (e, data) {
    totalDuration = +player.duration().toFixed(0);
  });
  player.on('tracking:seek', function (e, data) {
    seekCount = data.seekCount;
  });
  player.on('tracking:pause', function (e, data) {
    pauseCount = data.pauseCount;
  });
  player.on('tracking:buffered', function (e, data) {
    bufferCount = data.bufferCount;

    bufferDuration = bufferDuration + data.secondsToLoad;
  });
};

/**
 * Track Initial Play Event
 * This event is triggered when the video has been played for the first time.
 * If you are looking to track play events, simply listen on the player for a normal
 * "play" or "playing" event.
 *
 * Example Usage:
 * player.on('tracking:firstplay', (e, data) => console.log(data))
 *
 * Data Attributes:
 * => secondsToLoad: Total number of seconds between the player initializing
 *                   a play request and when the first frame begins.
 *
 * @function PlayTracking
 * @param    {Object} [config={}]
 *           An object of config left to the plugin author to define.
 */

var PlayTracking = function PlayTracking(config) {
  var player = this;
  var firstplay = false;
  var loadstart = 0;
  var loadend = 0;
  var secondsToLoad = 0;
  var timestamps = [];
  var stats_interval = config['x-duration'];

  var reset = function reset() {
    firstplay = false;
    loadstart = 0;
    loadend = 0;
    timestamps = [];
  };

  player.on('dispose', reset);
  player.on('ended', reset);
  player.on('pause', function () {
    timestamps = [];
  });
  player.on('loadstart', function () {
    //sendStats();
    firstplay = false;
    loadend = 0;
    loadstart = new Date();
    timestamps = [];
  });

  player.on('loadeddata', function () {
    loadend = new Date();
    secondsToLoad = (loadend - loadstart) / 1000;
  });

  player.on('timeupdate', function () {
    var curTime = +player.currentTime().toFixed(0);
    if (timestamps.indexOf(curTime) < 0) {
      timestamps.push(curTime);
      if (curTime % stats_interval == 0 || curTime == 1) {
        player.trigger('tracking:play', { timecode: curTime });
      }
    }
  });

  player.on('playing', function () {
    if (!firstplay) {
      firstplay = true;
      player.trigger('tracking:firstplay', { secondsToLoad: +secondsToLoad });
    }
  });
};

/**
 * Track Seeking Events
 * During playback, we are tracking how many times a person seeks, and
 * the position a user has seeked to.
 *
 * Example Usage:
 * player.on('tracking:seek', (e, data) => console.log(data))
 *
 * Data Attributes:
 * => seekCount: total number of seeks that has occuring during this file
 * => seekTo: Position, in seconds, that has been seeked to.
 *
 * @function SeekTracking
 * @param    {Object} [config={}]
 *           An object of config left to the plugin author to define.
 */
var SeekTracking = function SeekTracking(config) {
  var player = this;
  var seekCount = 0;
  var locked = true;
  var reset = function reset() {
    seekCount = 0;
    locked = true;
  };

  player.on('dispose', reset);
  player.on('loadstart', reset);
  player.on('ended', reset);
  player.on('play', function () {
    locked = false;
  });
  player.on('pause', function () {
    if (locked || !player.scrubbing()) {
      return;
    }
    var curTime = +player.currentTime().toFixed(0);
    seekCount++;
    player.trigger('tracking:seek', {
      seekCount: +seekCount,
      seekTo: curTime
    });
  });
};

/**
 * @function BufferTracking
 * @param    {Object} [config={}]
 *           An object of options left to the plugin author to define.
 *
 * Tracks when the video player is marked as buffering and waits until the player
 * has made some progress.
 *
 * Example Usage:
 * player.on('tracking:buffered', (e, data) => console.log(data))
 *
 * Data Attributes:
 * => currentTime:    current second of video playback
 * => readyState:     video#readyState value
 * => secondsToLoad:  Total amount of time in seconds buffering took
 * => bufferCount:    Total buffer events for this source
 */

var SendDataTracking = function SendDataTracking(config, http_Ressource) {
    var player = this;
    var pauseCount = 0;
    var seekCount = 0;
    var bufferDuration = false;
    var watchedDuration = 0;
    var initialLoadTime = 0;

    var reset = function reset(e) {
        pauseCount = 0;
        seekCount = 0;
        bufferDuration = 0;
        watchedDuration = 0;
        initialLoadTime = 0;
    };

    player.on('tracking:play', function (e, data) {
        getConfig();
        config["x-event"] = "play";
        config["timecode"] = data.timecode;
        config["pauseCount"] = data.pauseCount;
        sendData(config);
    });

    player.on('tracking:seek', function (e, data) {
        seekCount++;
        getConfig();
        config["x-event"] = "seek";
        config["seekCount"] = data.seekCount;
        config["timecode"] = +player.currentTime().toFixed(0);
        sendData(config);
    });
    player.on('tracking:pause', function (e, data) {
        pauseCount++;
        getConfig();
        config["x-event"] = "pause";
        config["pauseCount"] = data.pauseCount;
        config["timecode"] = +player.currentTime().toFixed(0);
        sendData(config);
    });
    player.on('tracking:buffered', function (e, data) {
        getConfig();
        sendData(config);
    });
    player.on('tracking:firstplay', function (e, data) {
        reset();
        getConfig();
        config["timecode"] = +player.currentTime().toFixed(0);
        config["x-event"] = "firstplay";
        config["v-id"] = getTheUniqueID();
        config["secondsToLoad"] = data.secondsToLoad;
        config["timecode"] = +player.currentTime().toFixed(0);
        sendData(config);
        //sendStats(config);
    });

    player.on('tracking:first-quarter', function (e, data) {
        config["x-event"] = "first-quarter";
        sendData(config);
    });
    player.on('tracking:second-quarter', function (e, data) {
        getConfig();
        config["x-event"] = "second-quarter";
        sendData(config);
    });
    player.on('tracking:third-quarter', function (e, data) {
        getConfig();
        config["x-event"] = "third-quarter";
        sendData(config);
    });
    player.on('tracking:fourth-quarter', function (e, data) {
        getConfig();
        config["x-event"] = "fourth-quarter";
        sendData(config);
    });

    player.on('tracking:performance', function (e, data) {
        config["seekCount"] = data.seekCount;
        config["pauseCount"] = data.pauseCount;
        config["bufferCount"] = data.bufferCount;
        config["totalDuration"] = data.totalDuration;
        config["bufferDuration"] = data.bufferDuration;
        config["watchedDuration"] = data.watchedDuration;
        config["initialLoadTime"] = data.initialLoadTime;
        config["timecode"] = +player.currentTime().toFixed(0);
        config["x-event"] = "performance";
        sendData(config);
    });

    function getTheUniqueID() {
        // always start with a letter (for DOM friendlyness)
        var idstr = String.fromCharCode(Math.floor(Math.random() * 25 + 65));
        do {
            // between numbers and characters (48 is 0 and 90 is Z (42-48 = 90)
            var ascicode = Math.floor(Math.random() * 42 + 48);
            if (ascicode < 58 || ascicode > 64) {
                // exclude all chars between : (58) and @ (64)
                idstr += String.fromCharCode(ascicode);
            }
        } while (idstr.length < 32);
        return idstr;
    }

    function sendData(dataStats) {

        http_Ressource.post(dataStats.stats_url + dataStats['x-app'], dataStats, {
            emulateJSON: true,
            headers: {
                'Accept': "text/json",
                "Content-Type": "application/x-www-form-urlencoded"
            }
        }).then(function (response) {}, function (response) {});
    }

    function getConfig() {
        config["seekCount"] = seekCount;
        config["pauseCount"] = pauseCount;
        config["bufferCount"] = bufferDuration;
        config["bufferDuration"] = bufferDuration;
        config["watchedDuration"] = watchedDuration;
        config["initialLoadTime"] = initialLoadTime;
    }
};

var asyncGenerator = function () {
  function AwaitValue(value) {
    this.value = value;
  }

  function AsyncGenerator(gen) {
    var front, back;

    function send(key, arg) {
      return new Promise(function (resolve, reject) {
        var request = {
          key: key,
          arg: arg,
          resolve: resolve,
          reject: reject,
          next: null
        };

        if (back) {
          back = back.next = request;
        } else {
          front = back = request;
          resume(key, arg);
        }
      });
    }

    function resume(key, arg) {
      try {
        var result = gen[key](arg);
        var value = result.value;

        if (value instanceof AwaitValue) {
          Promise.resolve(value.value).then(function (arg) {
            resume("next", arg);
          }, function (arg) {
            resume("throw", arg);
          });
        } else {
          settle(result.done ? "return" : "normal", result.value);
        }
      } catch (err) {
        settle("throw", err);
      }
    }

    function settle(type, value) {
      switch (type) {
        case "return":
          front.resolve({
            value: value,
            done: true
          });
          break;

        case "throw":
          front.reject(value);
          break;

        default:
          front.resolve({
            value: value,
            done: false
          });
          break;
      }

      front = front.next;

      if (front) {
        resume(front.key, front.arg);
      } else {
        back = null;
      }
    }

    this._invoke = send;

    if (typeof gen.return !== "function") {
      this.return = undefined;
    }
  }

  if (typeof Symbol === "function" && Symbol.asyncIterator) {
    AsyncGenerator.prototype[Symbol.asyncIterator] = function () {
      return this;
    };
  }

  AsyncGenerator.prototype.next = function (arg) {
    return this._invoke("next", arg);
  };

  AsyncGenerator.prototype.throw = function (arg) {
    return this._invoke("throw", arg);
  };

  AsyncGenerator.prototype.return = function (arg) {
    return this._invoke("return", arg);
  };

  return {
    wrap: function (fn) {
      return function () {
        return new AsyncGenerator(fn.apply(this, arguments));
      };
    },
    await: function (value) {
      return new AwaitValue(value);
    }
  };
}();















var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

// Cross-compatibility for Video.js 5 and 6.
var registerPlugin = videojs.registerPlugin || videojs.plugin;
var http_Ressource = {};

var newUser = "y";
var data_stats = {
    "type": 'intranet',
    "x-event": "connect",
    "timecode": 0,
    "c-proto": location.protocol,
    "client": "demo",
    "user_id": cookiesUserId(),
    "new_user": newUser,
    "c-referrer": location.href,
    "c-user-agent": navigator.userAgent,
    "x-app": "intranet",
    "v": "id",
    "v-id": "",
    "c-ip": "c_ip",
    "s-port": "s_port",
    "s-ip": "s-ip",
    "x-duration": 4,
    "stats_url": ""

};

var player = {};

function cookiesUserId() {
    var user_id = "";
    if (getCookie("uepUserID") == "") {
        user_id = guid();
        createCookie("uepUserID", user_id, 10 * 365);
        return user_id;
    } else {
        user_id = getCookie("uepUserID");
        newUser = "n";
        return user_id;
    }
}

function getCookie(c_name) {
    var c_start = "";
    var c_end = "";
    if (document.cookie.length > 0) {

        c_start = document.cookie.indexOf(c_name + "=");
        if (c_start != -1) {
            c_start = c_start + c_name.length + 1;
            c_end = document.cookie.indexOf(";", c_start);
            if (c_end == -1) {
                c_end = document.cookie.length;
            }
            return unescape(document.cookie.substring(c_start, c_end));
        }
    }
    return "";
}

function createCookie(name$$1, value, days) {
    var expires;
    if (days) {
        var date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toGMTString();
    } else {
        expires = "";
    }
    document.cookie = name$$1 + "=" + value + expires + "; path=/";
}

function guid() {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    }

    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
}

var statTrackingCall = function statTrackingCall(options, http_Ressource) {
    PauseTracking.apply(player, arguments);
    BufferTracking.apply(player, arguments);
    PercentileTracking.apply(player, arguments);
    PlayTracking.apply(player, arguments);
    SeekTracking.apply(player, arguments);
    PerformanceTracking.apply(player, arguments);
    SendDataTracking.apply(player, arguments);
};

/**
 * Event Tracking for VideoJS
 *
 * @function statTracking
 * @param    {Object} [options={}]
 *           An object of options left to the plugin author to define.
 */

var statTracking = function statTracking(options, httpRessource) {
    http_Ressource = httpRessource;
    var objectStats = _extends({}, data_stats, options);
    player = this;
    statTrackingCall(objectStats, http_Ressource);
};

// Register the plugin with video.js.
registerPlugin('statTracking', statTracking);

// Include the version number.
statTracking.VERSION = version;

module.exports = statTracking;



//////////////////
// WEBPACK FOOTER
// ./plugin/videojs_stat_tracking/dist/videojs-stat-tracking.cjs.js
// module id = 708
// module chunks = 0